import React from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { OptionProps } from 'react-select';

import type { IOption } from './types';
import { MOBILE_SELECT_VARIABLES } from './constants';

import { filterProps } from 'utils/helpers';

enum OPTION_VARIABLES {
  color = '--color',
}

const Container = styled('div', filterProps('isActive'))<{ isActive: boolean }>`
  ${({ theme: { durations }, isActive }) => css`
    ${OPTION_VARIABLES.color}: ${isActive
      ? `var(${MOBILE_SELECT_VARIABLES.activeTextColor}) `
      : `var(${MOBILE_SELECT_VARIABLES.textColor}) `};

    box-sizing: border-box;
    display: grid;
    position: relative;
    grid-template-columns: min-content 1fr;
    column-gap: 2rem;
    align-items: center;
    justify-content: flex-start;
    background-color: var(${MOBILE_SELECT_VARIABLES.backgroundColor});
    cursor: pointer;
    padding: 1.5rem 2rem;
    width: 100%;
    user-select: none;

    &::before {
      display: block;
      position: absolute;
      top: 0;
      left: 0;
      transition: opacity ${durations.regular};
      opacity: 0;
      background-color: var(${MOBILE_SELECT_VARIABLES.textColor});
      width: 100%;
      height: 100%;
      content: '';
    }

    &:hover {
      background-color: transparent;

      &::before {
        opacity: 0.1;
      }
    }

    &:active {
      background-color: transparent;

      &::before {
        opacity: 0.2;
      }
    }
  `}
`;

const Icon = styled('div', filterProps('url'))<{
  url: string;
}>`
  ${({ url }) => css`
    background-color: var(${OPTION_VARIABLES.color});
    mask-image: url(${url});
    mask-repeat: no-repeat;
    mask-position: center;
    mask-size: contain;
    width: 3rem;
    height: 3rem;
  `}
`;

const Label = styled.span`
  ${({ theme: { tp } }) => css`
    margin: 0;
    line-height: 1.2;
    color: var(${OPTION_VARIABLES.color});
    font-size: 1.75rem;

    ${tp.useFont(fonts => fonts.Switzer.regular)};
  `}
`;

const Option: React.FC<OptionProps<IOption, false>> = props => {
  const {
    innerRef,
    innerProps,
    data: { label, icon },
    isSelected = false,
  } = props;

  return (
    <Container ref={innerRef} {...innerProps} isActive={isSelected}>
      {icon ? <Icon url={icon} /> : null}
      <Label>{label}</Label>
    </Container>
  );
};

export default Option;
